import type { FC } from "react";
import type { ObjectKind } from "@engine/types";
import type { IconProps } from "../types";

/**
 * The classic set: our own hand-drawn furniture, moved here from FurnitureIcon when themes
 * arrived. Simple open shapes for pieces you can stand on, solid ones for pieces you cannot.
 */

type Draw = FC<IconProps>;

/** The measurements every drawing shares. */
function frame({ x, y, size, occupiable, palette }: IconProps) {
  const stroke = occupiable ? palette.stroke : palette.strokeBlocking;
  const fill = occupiable ? "none" : palette.fill;
  const pad = size * 0.2;
  const inner = size - pad * 2;
  const cx = x + size / 2;
  const cy = y + size / 2;
  const common = { stroke, fill, strokeWidth: 1.4, vectorEffect: "non-scaling-stroke" as const };
  return { stroke, fill, pad, inner, cx, cy, common };
}

const Bed: Draw = (props) => {
  const { pad, inner, common } = frame(props);
  const { x, y } = props;
  return (
    <>
      <rect x={x + pad} y={y + pad + 3} width={inner} height={inner - 6} rx={2} {...common} />
      <rect x={x + pad + 2} y={y + pad + 5} width={inner * 0.32} height={inner - 10} rx={1.5} {...common} />
    </>
  );
};

const Chair: Draw = (props) => {
  const { inner, cx, cy, common } = frame(props);
  return (
    <>
      <rect x={cx - inner * 0.28} y={cy - inner * 0.2} width={inner * 0.56} height={inner * 0.5} rx={1.5} {...common} />
      <line x1={cx - inner * 0.28} y1={cy - inner * 0.2} x2={cx - inner * 0.28} y2={cy - inner * 0.45} {...common} />
      <line x1={cx + inner * 0.28} y1={cy - inner * 0.2} x2={cx + inner * 0.28} y2={cy - inner * 0.45} {...common} />
    </>
  );
};

const Carpet: Draw = (props) => {
  const { pad, common } = frame(props);
  const { x, y, size } = props;
  return (
    <rect
      x={x + pad * 0.7}
      y={y + pad * 0.7}
      width={size - pad * 1.4}
      height={size - pad * 1.4}
      rx={2}
      strokeDasharray="3 2"
      {...common}
    />
  );
};

const Bench: Draw = (props) => {
  const { pad, inner, cy, common } = frame(props);
  const { x, size } = props;
  return <rect x={x + pad * 0.6} y={cy - inner * 0.16} width={size - pad * 1.2} height={inner * 0.32} rx={2} {...common} />;
};

const Table: Draw = (props) => {
  const { pad, inner, cy, common } = frame(props);
  const { x, y, size, palette } = props;
  return (
    <>
      <rect x={x + pad} y={cy - inner * 0.22} width={inner} height={inner * 0.34} rx={2} {...common} fill={palette.fill} />
      <line x1={x + pad + 3} y1={cy + inner * 0.12} x2={x + pad + 3} y2={y + size - pad} {...common} />
      <line x1={x + size - pad - 3} y1={cy + inner * 0.12} x2={x + size - pad - 3} y2={y + size - pad} {...common} />
    </>
  );
};

const Cabinet: Draw = (props) => {
  const { pad, inner, cx, common } = frame(props);
  const { x, y, size, palette } = props;
  return (
    <>
      <rect x={x + pad} y={y + pad} width={inner} height={inner} rx={1.5} {...common} fill={palette.fill} />
      <line x1={cx} y1={y + pad} x2={cx} y2={y + size - pad} {...common} />
    </>
  );
};

const Plant: Draw = (props) => {
  const { inner, cx, cy, common } = frame(props);
  const { palette } = props;
  return (
    <>
      <path d={`M${cx} ${cy + inner * 0.35} L${cx} ${cy - inner * 0.1}`} {...common} fill="none" />
      <circle cx={cx} cy={cy - inner * 0.22} r={inner * 0.26} {...common} fill={palette.fill} />
    </>
  );
};

const Tree: Draw = (props) => {
  const { inner, cx, cy, common } = frame(props);
  const { palette } = props;
  return (
    <>
      <rect x={cx - 2} y={cy} width={4} height={inner * 0.42} {...common} fill={palette.fill} />
      <circle cx={cx} cy={cy - inner * 0.12} r={inner * 0.36} {...common} fill={palette.fill} />
    </>
  );
};

const Statue: Draw = (props) => {
  const { inner, cx, cy, common } = frame(props);
  const { palette } = props;
  return (
    <>
      <polygon
        points={`${cx},${cy - inner * 0.38} ${cx + inner * 0.28},${cy + inner * 0.22} ${cx - inner * 0.28},${cy + inner * 0.22}`}
        {...common}
        fill={palette.fill}
      />
      <rect x={cx - inner * 0.34} y={cy + inner * 0.22} width={inner * 0.68} height={inner * 0.16} {...common} fill={palette.fill} />
    </>
  );
};

const Crate: Draw = (props) => {
  const { pad, inner, common } = frame(props);
  const { x, y, size, palette } = props;
  return (
    <>
      <rect x={x + pad} y={y + pad} width={inner} height={inner} rx={1} {...common} fill={palette.fill} />
      <line x1={x + pad} y1={y + pad} x2={x + size - pad} y2={y + size - pad} {...common} />
    </>
  );
};

const Pond: Draw = (props) => {
  const { inner, cx, cy, common } = frame(props);
  const { palette } = props;
  return <ellipse cx={cx} cy={cy} rx={inner * 0.48} ry={inner * 0.34} {...common} fill={palette.accent} />;
};

const Stove: Draw = (props) => {
  const { pad, inner, cx, cy, common } = frame(props);
  const { x, y, palette } = props;
  return (
    <>
      <rect x={x + pad} y={y + pad} width={inner} height={inner} rx={2} {...common} fill={palette.fill} />
      <circle cx={cx - inner * 0.18} cy={cy - inner * 0.16} r={inner * 0.12} {...common} fill="none" />
      <circle cx={cx + inner * 0.18} cy={cy + inner * 0.16} r={inner * 0.12} {...common} fill="none" />
    </>
  );
};

export const classicIcons: Record<ObjectKind, Draw> = {
  bed: Bed,
  chair: Chair,
  carpet: Carpet,
  bench: Bench,
  table: Table,
  cabinet: Cabinet,
  plant: Plant,
  tree: Tree,
  statue: Statue,
  crate: Crate,
  pond: Pond,
  stove: Stove,
};
